import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config/api';

interface AlternativeKeyword {
  keyword: string;
  isExisting: boolean;
}

interface AlternativeKeywordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyword: string;
  onKeywordsAdded: () => void;
}

const AlternativeKeywordsModal: React.FC<AlternativeKeywordsModalProps> = ({
  isOpen,
  onClose,
  keyword,
  onKeywordsAdded
}) => {
  const [alternatives, setAlternatives] = useState<AlternativeKeyword[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingKeywords, setAddingKeywords] = useState<Set<string>>(new Set());
  const [addedKeywords, setAddedKeywords] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<{ total: number; new: number; existing: number } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (isOpen && keyword) {
      fetchAlternatives();
    }
  }, [isOpen, keyword]);

  const fetchAlternatives = async () => {
    setLoading(true);
    setError(null);
    setAlternatives([]);
    setAddedKeywords(new Set());

    try {
      const response = await fetch(`${API_URL}/api/keywords/alternatives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keyword }),
      });

      const data = await response.json();

      if (data.success) {
        setAlternatives(data.data.alternatives);
        setStats(data.stats);
      } else {
        setError(data.error || 'Failed to fetch alternatives');
      }
    } catch (err) {
      console.error('Error fetching alternatives:', err);
      setError('Failed to fetch alternatives');
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreAlternatives = async () => {
    setLoadingMore(true);
    setError(null);

    try {
      // Get current keywords to exclude
      const currentKeywords = alternatives.map(alt => alt.keyword);
      
      const response = await fetch(`${API_URL}/api/keywords/alternatives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          keyword, 
          excludeKeywords: currentKeywords 
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Add new alternatives to existing ones
        const newAlternatives = data.data.alternatives.filter((newAlt: AlternativeKeyword) => 
          !alternatives.some(existingAlt => existingAlt.keyword === newAlt.keyword)
        );
        
        setAlternatives(prev => [...prev, ...newAlternatives]);
        
        // Update stats
        if (stats) {
          const newTotal = stats.total + newAlternatives.length;
          const newNew = stats.new + newAlternatives.filter((alt: AlternativeKeyword) => !alt.isExisting).length;
          const newExisting = stats.existing + newAlternatives.filter((alt: AlternativeKeyword) => alt.isExisting).length;
          
          setStats({
            total: newTotal,
            new: newNew,
            existing: newExisting
          });
        }
      } else {
        setError(data.error || 'Failed to fetch more alternatives');
      }
    } catch (err) {
      console.error('Error fetching more alternatives:', err);
      setError('Failed to fetch more alternatives');
    } finally {
      setLoadingMore(false);
    }
  };

  const addKeyword = async (keywordToAdd: string) => {
    setAddingKeywords(prev => new Set(prev).add(keywordToAdd));

    try {
      const response = await fetch(`${API_URL}/api/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keyword: keywordToAdd,
          language: 'en'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setAddedKeywords(prev => new Set(prev).add(keywordToAdd));
        // Update the isExisting flag in alternatives
        setAlternatives(prev =>
          prev.map(alt =>
            alt.keyword === keywordToAdd
              ? { ...alt, isExisting: true }
              : alt
          )
        );

        // Update stats
        if (stats) {
          setStats({
            ...stats,
            new: stats.new - 1,
            existing: stats.existing + 1
          });
        }
      } else {
        alert(data.error || 'Failed to add keyword');
      }
    } catch (err) {
      console.error('Error adding keyword:', err);
      alert('Failed to add keyword');
    } finally {
      setAddingKeywords(prev => {
        const next = new Set(prev);
        next.delete(keywordToAdd);
        return next;
      });
    }
  };

  const addAllKeywords = async () => {
    const newKeywords = alternatives.filter(alt => !alt.isExisting && !addedKeywords.has(alt.keyword));

    if (newKeywords.length === 0) {
      alert('No new keywords to add');
      return;
    }

    if (!window.confirm(`Add ${newKeywords.length} new alternative keywords?`)) {
      return;
    }

    for (const alt of newKeywords) {
      await addKeyword(alt.keyword);
    }

    onKeywordsAdded();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20000,
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '24px',
          minWidth: '600px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border-color)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid var(--border-color)',
          paddingBottom: '12px'
        }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px' }}>
              🔄 Alternative Keywords for: "{keyword}"
            </h3>
            {stats && !loading && (
              <p style={{
                margin: '8px 0 0 0',
                fontSize: '14px',
                color: 'var(--text-secondary)'
              }}>
                {stats.total} alternatives • {stats.new} new • {stats.existing} existing
              </p>
            )}
          </div>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            style={{ padding: '6px 12px' }}
          >
            ✕
          </button>
        </div>


        {/* Action Buttons */}
        {!loading && (
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginBottom: '16px' 
          }}>
            {/* Add All Button */}
            {stats && stats.new > 0 && (
              <button
                className="btn btn-primary"
                onClick={addAllKeywords}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)'
                }}
              >
                Add All New ({stats.new})
              </button>
            )}
            
            {/* More Button */}
            {alternatives.length > 0 && (
              <button
                className="btn btn-secondary"
                onClick={fetchMoreAlternatives}
                disabled={loadingMore}
                style={{
                  padding: '8px 16px',
                  minWidth: '120px',
                  background: loadingMore ? 'var(--bg-secondary)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}
              >
                {loadingMore ? (
                  <>
                    <div 
                      className="spinner" 
                      style={{ 
                        width: '16px', 
                        height: '16px', 
                        display: 'inline-block',
                        marginRight: '8px'
                      }} 
                    />
                    Loading...
                  </>
                ) : (
                  '🔄 More +20'
                )}
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: '8px'
        }}>
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px'
            }}>
              <div className="spinner" />
            </div>
          )}

          {error && (
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--error-bg)',
              color: 'var(--error-text)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && alternatives.length > 0 && (
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}>
              <h4 style={{
                margin: '0 0 12px 0',
                color: 'var(--text-primary)',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>💡</span>
                <span>Alternative Keywords</span>
              </h4>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {alternatives.map((alt) => (
                  <div
                    key={alt.keyword}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      backgroundColor: 'var(--bg-primary)',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)'
                    }}
                  >
                    <span style={{
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}>
                      {alt.keyword}
                    </span>

                    {alt.isExisting || addedKeywords.has(alt.keyword) ? (
                      <span style={{
                        color: '#4CAF50',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        ✓ Added
                      </span>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => addKeyword(alt.keyword)}
                        disabled={addingKeywords.has(alt.keyword)}
                        style={{
                          padding: '2px 8px',
                          fontSize: '12px',
                          minWidth: '50px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        }}
                      >
                        {addingKeywords.has(alt.keyword) ? '...' : 'Add'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && !error && alternatives.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: 'var(--text-secondary)'
            }}>
              No alternatives found
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AlternativeKeywordsModal;